import React, { useState, useEffect } from 'react';
import { hasPermission, getUserType, getCurrentUser } from '../services/authService';
import api from '../services/api';
import { ActiveTab } from '../types';
import ImagenSelectorModal from './ImagenSelectorModal';
import { Button, Modal, LoadingState, EmptyState } from './ui';
import NeaLogo from './NeaLogo';
import { formatFecha, tieneCoordenada } from '../utils/fecha';
import { cargarImagenComoDataUri } from '../utils/imagenes';
import { ExclamationTriangleIcon, CheckCircleIcon } from '@heroicons/react/24/outline';

// Reutilizamos las interfaces de CrearAnalisis
interface EnsayoData {
  id: number;
  analito: string;
  analito_id: string;
  resultado: string;
  unidad: string;
  metodo: string;
  limite_deteccion: string;
}

interface MuestraData {
  id: number;
  id_nea: string;
  id_m_origen: string;
  id_m_lab: string;
  id_muestra_lab: string;
  responsable: string;
  pertenencia: string;
  sitio_muestreo: string;
  georr_lat: string;
  georr_long: string;
  medio: string;
  fecha_muestreo: string;
  ensayos: EnsayoData[];
  mostrar_organismo_control: boolean;
  organismo_control_id: number | null;
  organismo_control: string;
  mostrar_cadena_custodia: boolean;
  cadena_custodia: string;
  remito?: string;
  descripcion?: string;
}

interface FormDataType {
  /** Número NEA del análisis, el identificador que se comunica al cliente. */
  numero_nea: string;
  id_cliente: number;
  id_laboratorio: number;
  id_origen: number;
  fecha_muestreo: string;
  fecha_recepcion: string;
  fecha_analisis: string;
  fecha_emision: string;
  organismo_control: boolean;
  cadena_custodia: boolean;
  muestras: MuestraData[];
  // Campos del solicitante
  solicitante: string;
  cuit_sol: string;
  direccion_sol: string;
  // Campos del laboratorio
  laboratorio: string;
  cuit_lab: string;
  direccion_lab: string;
  // Campos adicionales
  planta: string;
  mostrar_numero_origen: boolean;
  numero_analisis_origen: string;
  mostrar_numero_laboratorio: boolean;
  numero_analisis_laboratorio: string;
  analistas?: string;
  firma?: string;
  director?: string;
  logo?: string;
}

interface CargarEnsayosProps {
  analisisId?: string;
  onNavigate?: (tab: ActiveTab, id?: string) => void;
}

const CargarEnsayos: React.FC<CargarEnsayosProps> = ({ analisisId, onNavigate }) => {
  const [analisis, setAnalisis] = useState<FormDataType | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  // Aviso de campos obligatorios. Va aparte de `error`, que reemplaza la pantalla.
  const [validacion, setValidacion] = useState<string | null>(null);
  const tienePermisoCargaEnsayos = hasPermission('p_carga');
  const currentUser = getCurrentUser();
  const [analistasTexto, setAnalistasTexto] = useState<string[]>([]);
  const analistasInputRefs = React.useRef<(HTMLInputElement | null)[]>([]);
  const [showFirmaModal, setShowFirmaModal] = useState(false);
  const [showLogoModal, setShowLogoModal] = useState(false);
  const [showLabFirmasModal, setShowLabFirmasModal] = useState(false);
  const [firmasLaboratorio, setFirmasLaboratorio] = useState<any[]>([]);
  const [loadingFirmas, setLoadingFirmas] = useState(false);
  // La firma y el logo se muestran incrustados, igual que en el informe: así se
  // ve acá lo mismo que va a salir en el PDF.
  const [firmaSrc, setFirmaSrc] = useState<string | null>(null);
  const [logoSrc, setLogoSrc] = useState<string | null>(null);

  // Usar el ID pasado como prop, o intentar obtenerlo de la URL si se está usando en una ruta
  const id = analisisId || new URLSearchParams(window.location.search).get('id') || '';

  useEffect(() => {
    // Verificar permisos
    if (!tienePermisoCargaEnsayos) {
      setError('No tienes permiso para cargar ensayos');
      setLoading(false);
      return;
    }

    // Si no hay ID, mostrar error
    if (!id) {
      setError('No se especificó un ID de análisis');
      setLoading(false);
      return;
    }

    // Cargar datos del análisis
    const cargarAnalisis = async () => {
      try {
        setLoading(true);
        console.log(`Solicitando análisis con ID: ${id}`);
        const response = await api.get(`/analisis/${id}`);
        
        console.log("Datos recibidos del API:", response.data);
        
        // Convertir los datos de la API al formato del formulario
        const analisisData = response.data;

        // Verificar si el análisis está en estado pendiente (estado 0)
        if (analisisData.estado !== 0) {
          setError(`Este análisis no está en estado pendiente. Solo se pueden cargar ensayos para análisis pendientes.`);
          setLoading(false);
          return;
        }

        // Obtener datos de cliente y laboratorio
        let clienteInfo = { nombre: '', cuit: '', direccion: '', planta: '' };
        let laboratorioInfo = { nombre: '', cuit: '', direccion: '' };
        
        // Mapa para almacenar nombres de analitos, medios y organismos por ID
        const analitosMap = new Map();
        const mediosMap = new Map();
        const organismosMap = new Map();
        
        try {
          // Cargar analitos
          try {
            const ensayosResponse = await api.get('/ensayos');
            if (ensayosResponse.data) {
              ensayosResponse.data.forEach((ensayo: any) => {
                analitosMap.set(ensayo.id.toString(), ensayo.analito);
              });
            }
          } catch (err) {
            console.error('Error al cargar analitos:', err);
          }
          
          // Cargar medios
          try {
            const mediosResponse = await api.get('/ensayos-medio');
            if (mediosResponse.data) {
              mediosResponse.data.forEach((medio: any) => {
                mediosMap.set(medio.id.toString(), medio.medio);
              });
            }
          } catch (err) {
            console.error('Error al cargar medios:', err);
          }
          
          // Cargar organismos de control
          try {
            const organismosResponse = await api.get('/organismos-control');
            if (organismosResponse.data) {
              organismosResponse.data.forEach((org: any) => {
                organismosMap.set(org.id.toString(), org.organismo);
              });
            }
          } catch (err) {
            console.error('Error al cargar organismos de control:', err);
          }
          
          // Obtener datos del cliente
          if (analisisData.id_origen) {
            const clienteResponse = await api.get(`/clientes/${analisisData.id_origen}`);
            if (clienteResponse.data) {
              clienteInfo = {
                nombre: clienteResponse.data.nombre || clienteResponse.data.razon_social || `Cliente ID: ${analisisData.id_origen}`,
                cuit: clienteResponse.data.cuit || '',
                direccion: clienteResponse.data.direccion ? 
                  `${clienteResponse.data.direccion}, ${clienteResponse.data.ciudad || ''}, ${clienteResponse.data.provincia || ''}` : '',
                planta: ''
              };
            }
          }
          
          // Obtener datos del laboratorio
          if (analisisData.id_lab) {
            const labResponse = await api.get(`/laboratorios/${analisisData.id_lab}`);
            if (labResponse.data) {
              laboratorioInfo = {
                nombre: labResponse.data.nombre || labResponse.data.razon_social || `Lab ID: ${analisisData.id_lab}`,
                cuit: labResponse.data.cuit || '',
                direccion: labResponse.data.direccion ? 
                  `${labResponse.data.direccion}, ${labResponse.data.ciudad || ''}, ${labResponse.data.provincia || ''}` : ''
              };
            }
          }
          
          // Obtener información de la planta si existe
          if (analisisData.id_planta && analisisData.id_planta > 0) {
            const plantaResponse = await api.get(`/plantas/${analisisData.id_planta}`);
            if (plantaResponse.data) {
              clienteInfo.planta = plantaResponse.data.denominacion || '';
            }
          }
        } catch (infoError) {
          console.error('Error al obtener información adicional:', infoError);
        }
        
        // Aquí organizamos los datos recibidos para que coincidan con la estructura de FormDataType
        const formattedData: FormDataType = {
          numero_nea: analisisData.id_a_nea || '',
          id_cliente: analisisData.id_origen || 0,
          id_laboratorio: analisisData.id_lab || 0,
          id_origen: analisisData.id_origen || 0,
          fecha_muestreo: '',
          fecha_recepcion: '',
          fecha_analisis: formatFecha(analisisData.fecha_solicitud),
          fecha_emision: '',
          organismo_control: !!analisisData.organismo_control,
          cadena_custodia: !!analisisData.cadena_custodia,
          muestras: analisisData.muestra?.map((m: any) => ({
            id: m.id,
            id_nea: m.id_m_nea || '',
            id_m_origen: m.id_m_origen || '',
            id_m_lab: m.id_m_lab || '',
            id_muestra_lab: m.id_muestra_lab || '',
            responsable: m.responsable || '',
            pertenencia: m.pertenencia || '',
            sitio_muestreo: m.sitio_muestreo?.denominacion || '',
            georr_lat: m.sitio_muestreo?.georreferencia_lat?.toString() || '',
            georr_long: m.sitio_muestreo?.georreferencia_long?.toString() || '',
            medio: mediosMap.get(m.medio) || m.medio || '',
            fecha_muestreo: formatFecha(m.fecha_muestreo),
            ensayos: m.ensayo?.map((e: any) => ({
              id: e.id,
              analito_id: e.id_ensayo?.toString() || '',
              analito: e.analito || analitosMap.get(e.id_ensayo?.toString()) || `Ensayo ${e.id_ensayo ?? ''}`.trim(),
              resultado: e.valor?.toString() || '',
              unidad: e.unidad || '',
              metodo: e.metodo_ensayo || '',
              limite_deteccion: e.limite_deteccion?.toString() || ''
            })) || [],
            mostrar_organismo_control: !!m.organismo,
            organismo_control_id: m.organismo ? m.organismo : null,
            organismo_control: m.organismo ? organismosMap.get(m.organismo.toString()) || `Organismo ${m.organismo}` : '',
            mostrar_cadena_custodia: !!m.custodia,
            cadena_custodia: m.custodia || '',
            remito: m.remito || '',
            descripcion: m.observaciones || ''
          })) || [],
          // Datos del solicitante (cliente)
          solicitante: clienteInfo.nombre,
          cuit_sol: clienteInfo.cuit,
          direccion_sol: clienteInfo.direccion,
          // Datos del laboratorio
          laboratorio: laboratorioInfo.nombre,
          cuit_lab: laboratorioInfo.cuit,
          direccion_lab: laboratorioInfo.direccion,
          // Datos adicionales
          planta: clienteInfo.planta,
          mostrar_numero_origen: !!analisisData.id_a_cliente,
          numero_analisis_origen: analisisData.id_a_cliente || '',
          mostrar_numero_laboratorio: !!analisisData.id_a_lab,
          numero_analisis_laboratorio: analisisData.id_a_lab || '',
          analistas: analisisData.analistas || '',
          firma: analisisData.firma || '',
          director: analisisData.director || '',
          logo: analisisData.logo || '',
        };
        
        console.log("Datos formateados para el formulario:", formattedData);
        setAnalisis(formattedData);
        setLoading(false);
      } catch (error) {
        console.error('Error al cargar análisis:', error);
        
        // En entorno de desarrollo, cargamos datos de ejemplo para pruebas
        if (process.env.NODE_ENV !== 'production') {
          console.log('Cargando datos de ejemplo para tests ya que el análisis con ID '+id+' no se pudo cargar');
          
          // Datos de ejemplo estructurados según el tipo FormDataType
          const datosEjemplo: FormDataType = {
            numero_nea: 'NEA/0',
            id_cliente: 1,
            id_laboratorio: 2,
            id_origen: 1,
            fecha_muestreo: new Date().toISOString().split('T')[0],
            fecha_recepcion: new Date().toISOString().split('T')[0],
            fecha_analisis: new Date().toISOString().split('T')[0],
            fecha_emision: new Date().toISOString().split('T')[0],
            organismo_control: false,
            cadena_custodia: true,
            muestras: [
              {
                id: 1,
                id_nea: 'NEA-2023-001',
                id_m_origen: 'ORIG-001',
                id_m_lab: '',
                id_muestra_lab: '',
                responsable: 'Juan Pérez',
                pertenencia: 'Empresa Cliente',
                sitio_muestreo: 'Zona Norte - Sitio 1',
                georr_lat: '-34.5678',
                georr_long: '-58.4567',
                medio: 'Agua superficial',
                fecha_muestreo: new Date().toISOString().split('T')[0],
                ensayos: [
                  {
                    id: 1,
                    analito: 'pH',
                    analito_id: '1',
                    resultado: '',
                    unidad: 'unidades de pH',
                    metodo: 'SM 4500-H+ B',
                    limite_deteccion: '0.01'
                  },
                  {
                    id: 2,
                    analito: 'Conductividad',
                    analito_id: '2',
                    resultado: '',
                    unidad: 'μS/cm',
                    metodo: 'SM 2510 B',
                    limite_deteccion: '1'
                  },
                  {
                    id: 3,
                    analito: 'Nitratos',
                    analito_id: '3',
                    resultado: '',
                    unidad: 'mg/L',
                    metodo: 'SM 4500-NO3- E',
                    limite_deteccion: '0.5'
                  }
                ],
                mostrar_organismo_control: false,
                organismo_control_id: null,
                organismo_control: '',
                mostrar_cadena_custodia: true,
                cadena_custodia: 'CC-2023-001',
                remito: 'REM-001-2023',
                descripcion: 'Muestra tomada según protocolo estándar'
              },
              {
                id: 2,
                id_nea: 'NEA-2023-002',
                id_m_origen: 'ORIG-002',
                id_m_lab: '',
                id_muestra_lab: '',
                responsable: 'María González',
                pertenencia: 'Empresa Cliente',
                sitio_muestreo: 'Zona Norte - Sitio 2',
                georr_lat: '-34.5789',
                georr_long: '-58.4678',
                medio: 'Agua residual',
                fecha_muestreo: new Date().toISOString().split('T')[0],
                ensayos: [
                  {
                    id: 4,
                    analito: 'DBO',
                    analito_id: '4',
                    resultado: '',
                    unidad: 'mg/L',
                    metodo: 'SM 5210 B',
                    limite_deteccion: '2'
                  },
                  {
                    id: 5,
                    analito: 'DQO',
                    analito_id: '5',
                    resultado: '',
                    unidad: 'mg/L',
                    metodo: 'SM 5220 D',
                    limite_deteccion: '5'
                  }
                ],
                mostrar_organismo_control: true,
                organismo_control_id: 3,
                organismo_control: 'Organismo de Control 3',
                mostrar_cadena_custodia: true,
                cadena_custodia: 'CC-2023-002',
                remito: 'REM-002-2023',
                descripcion: 'Muestra de efluente industrial'
              }
            ],
            solicitante: 'Empresa Industrial S.A.',
            cuit_sol: '30-12345678-9',
            direccion_sol: 'Av. Rivadavia 1234, CABA',
            laboratorio: 'Laboratorio Ambiental Certificado',
            cuit_lab: '30-98765432-1',
            direccion_lab: 'Av. Corrientes 5678, CABA',
            planta: 'Planta de Producción Norte',
            mostrar_numero_origen: true,
            numero_analisis_origen: 'A-ORIG-2023-001',
            mostrar_numero_laboratorio: true,
            numero_analisis_laboratorio: 'LAB-2023-001',
            analistas: 'Analista 1, Analista 2',
            firma: 'https://example.com/firma.jpg',
            director: 'Dr. Juan Pérez'
          };
          
          setAnalisis(datosEjemplo);
        } else {
          // En producción, mostrar mensaje de error al usuario
          setError(`Error al cargar el análisis ID ${id}. El servidor respondió con un error.`);
        }
        
        setLoading(false);
      }
    };

    cargarAnalisis();
  }, [id, tienePermisoCargaEnsayos]);

  // Carga inicial de la lista de analistas a partir del análisis. A partir de
  // ahí `analistasTexto` es la única fuente: se guarda al finalizar.
  const analistasCargados = React.useRef(false);
  useEffect(() => {
    if (!analisis || analistasCargados.current) return;
    analistasCargados.current = true;
    const guardados = (analisis.analistas || '')
      .split(';')
      .map(nombre => nombre.trim())
      .filter(nombre => nombre !== '' && nombre !== 'No especifica');
    setAnalistasTexto(guardados.length > 0 ? guardados : ['']);
  }, [analisis]);

  // Función para manejar el cambio de límite de detección
  const handleLimiteDeteccionChange = (muestraIndex: number, ensayoIndex: number, value: string) => {
    if (!analisis || !analisis.muestras) return;
    
    // Permitir números decimales, incluyendo valores muy pequeños como 0.000001
    // Regla: permitir números, punto decimal, y el campo vacío
    if (value === '' || /^-?\d*\.?\d*$/.test(value)) {
      const newFormData = { ...analisis };
      
      // Si es un valor numérico, validamos su rango
      if (value !== '') {
        const parsedValue = Number(value);
        
        // Solo validamos el máximo para evitar desbordamiento
        // Pero permitimos cualquier valor decimal pequeño positivo
        if (!isNaN(parsedValue)) {
          if (parsedValue > 999999) {
            // Limitar a un máximo de 999999 para evitar errores de rango
            newFormData.muestras[muestraIndex].ensayos[ensayoIndex].limite_deteccion = '999999';
            setAnalisis(newFormData as FormDataType);
            return;
          } else if (parsedValue < 0) {
            // Evitar valores negativos
            newFormData.muestras[muestraIndex].ensayos[ensayoIndex].limite_deteccion = '0';
            setAnalisis(newFormData as FormDataType);
            return;
          }
        }
      }
      
      // Si pasa las validaciones, actualizar con el valor original
      newFormData.muestras[muestraIndex].ensayos[ensayoIndex].limite_deteccion = value;
      setAnalisis(newFormData as FormDataType);
    }
  };

  // Función para manejar el cambio en los resultados de ensayo
  const handleResultadoChange = (muestraIndex: number, ensayoIndex: number, value: string) => {
    // Permitir números, puntos decimales y el campo vacío
    if (value === '' || /^-?\d*\.?\d*$/.test(value)) {
      handleUpdateEnsayo(muestraIndex, ensayoIndex, 'resultado', value);
    }
  };

  const handleUpdateEnsayo = (muestraIndex: number, ensayoIndex: number, field: string, value: string) => {
    if (!analisis) return;

    setAnalisis(prevState => {
      if (!prevState) return prevState;

      const updatedMuestras = [...prevState.muestras];
      const updatedEnsayos = [...updatedMuestras[muestraIndex].ensayos];
      
      updatedEnsayos[ensayoIndex] = {
        ...updatedEnsayos[ensayoIndex],
        [field]: value
      };
      
      updatedMuestras[muestraIndex] = {
        ...updatedMuestras[muestraIndex],
        ensayos: updatedEnsayos
      };
      
      return {
        ...prevState,
        muestras: updatedMuestras
      };
    });
  };

  const handleSave = async () => {
    if (!analisis) return;
    
    try {
      setSaving(true);
      
      // Preparar los datos para enviar al servidor
      const updatedMuestras = analisis.muestras.map(muestra => {
        // Obtener los ensayos actualizados
        const updatedEnsayos = muestra.ensayos.map(ensayo => ({
          id: ensayo.id,
          id_muestra: muestra.id,
          id_ensayo: ensayo.analito_id,
          valor: ensayo.resultado,
          unidad: ensayo.unidad,
          organismo: "FISICOQUIMICO",
          metodo_ensayo: ensayo.metodo,
          limite_deteccion: ensayo.limite_deteccion
        }));
        
        // La fecha de muestreo quedó fijada al solicitar el análisis: no se
        // reenvía, para no pisarla con la fecha del día al cerrar el informe.
        return {
          id: muestra.id,
          observaciones: muestra.descripcion || '',
          ensayos: updatedEnsayos
        };
      });
      
      // Formatear analistas
      const analistasFormateados = analistasTexto
        .filter(a => a.trim() !== '')
        .join(';');

      // Datos a enviar al API
      const dataToSend = {
        muestras: updatedMuestras,
        analistas: analistasFormateados,
        director: (analisis.director || '').trim(),
        firma: analisis.firma || 'no especifica.png',
        logo: analisis.logo || 'no especifica.png',
        titulo: 'Análisis',
        usuario_finalizador: currentUser?.id || null,
        estado: 1
      };
      
      console.log("Enviando datos actualizados:", JSON.stringify(dataToSend, null, 2));
      console.log("ID de usuario finalizador:", currentUser?.id);
      console.log("ID de análisis:", id);
      
      try {
        // Primero verificar si existe el análisis
        console.log(`Verificando análisis con ID: ${id}`);
        const verificacion = await api.get(`/analisis/${id}`);
        console.log("Verificación de análisis:", verificacion.data);
      } catch (verificacionError) {
        console.error("Error al verificar análisis:", verificacionError);
        setError(`Error al verificar análisis con ID ${id}. Puede que no exista o no sea accesible.`);
        setSaving(false);
        return;
      }
      
      // Enviar datos al servidor
      console.log(`PUT a /analisis/${id}/ensayos con datos...`);
      const respuesta = await api.put(`/analisis/${id}/ensayos`, dataToSend);
      console.log("Respuesta del servidor:", respuesta.data);
      
      setSuccess(true);
      setSaving(false);
      
      // Navegar de vuelta después de guardar exitosamente
      setTimeout(() => {
        if (onNavigate) {
          onNavigate('analisis-pendientes');
        } else {
          window.location.href = `${process.env.PUBLIC_URL || ''}/`;
        }
      }, 2000);
    } catch (error: any) {
      console.error('Error al guardar análisis:', error);
      
      // Mostrar información más detallada del error
      if (error.response) {
        // El servidor respondió con un código de estado fuera del rango 2xx
        console.error('Respuesta del servidor:', error.response.status);
        console.error('Datos de error:', error.response.data);
        console.error('Headers:', error.response.headers);
        
        setError(`Error ${error.response.status}: ${
          typeof error.response.data === 'string' 
            ? error.response.data 
            : JSON.stringify(error.response.data)
        }`);
      } else if (error.request) {
        // La solicitud se realizó pero no se recibió respuesta
        console.error('Sin respuesta del servidor:', error.request);
        setError('No se recibió respuesta del servidor. Verifique su conexión e intente nuevamente.');
      } else {
        // Algo ocurrió en la configuración de la solicitud
        console.error('Error en la configuración:', error.message);
        setError(`Error en la configuración: ${error.message}`);
      }
      
      setSaving(false);
    }
  };

  // Funciones para manejar los analistas - Asegurarnos que preservan los espacios
  const handleAnalistaChange = (index: number, valor: string) => {
    const nuevosAnalistas = [...analistasTexto];
    nuevosAnalistas[index] = valor; // No hacer trim() aquí para preservar espacios
    setAnalistasTexto(nuevosAnalistas);
  };

  const agregarAnalista = () => {
    setAnalistasTexto([...analistasTexto, '']);
    
    // Focus en el nuevo campo después de la próxima renderización
    setTimeout(() => {
      const lastIndex = analistasTexto.length;
      if (analistasInputRefs.current[lastIndex]) {
        analistasInputRefs.current[lastIndex]?.focus();
      }
    }, 10);
  };

  const eliminarAnalista = (index: number) => {
    // Si solo queda un analista, dejarlo vacío en lugar de eliminarlo
    if (analistasTexto.length === 1) {
      handleAnalistaChange(0, '');
      return;
    }
    
    setAnalistasTexto(analistasTexto.filter((_, i) => i !== index));
  };

  // Un informe cerrado tiene que decir quién lo hizo: al menos un analista y el
  // director son obligatorios para finalizar.
  const confirmarGuardado = () => {
    if (analistasTexto.every(nombre => nombre.trim() === '')) {
      setValidacion('Cargue el nombre de al menos un analista para finalizar el análisis.');
      return;
    }
    if (!analisis?.director || analisis.director.trim() === '') {
      setValidacion('Cargue el nombre del director para finalizar el análisis.');
      return;
    }
    setValidacion(null);
    setShowConfirmModal(true);
  };

  const handleCancel = () => {
    if (onNavigate) {
      onNavigate('analisis-pendientes');
    } else {
      window.location.href = `${process.env.PUBLIC_URL || ''}/`;
    }
  };

  // Cargar firmas del laboratorio
  const cargarFirmasLaboratorio = async () => {
    if (!analisis) return;
    
    setLoadingFirmas(true);
    try {
      const response = await api.get(`/imagenes/firmas-laboratorio/${analisis.id_laboratorio}`);
      
      // Añadir timestamp a las URLs para evitar problemas de caché
      const firmasConTimestamp = response.data.map((firma: any) => {
        const timestamp = new Date().getTime();
        return {
          ...firma,
          urlConTimestamp: `${api.defaults.baseURL}${firma.url}?t=${timestamp}`
        };
      });
      
      setFirmasLaboratorio(firmasConTimestamp);
    } catch (error) {
      console.error('Error al cargar firmas del laboratorio:', error);
    } finally {
      setLoadingFirmas(false);
    }
  };

  // Funciones para manejar la selección de imágenes
  const handleSelectFirma = (nombreImagen: string) => {
    if (!analisis) return;
    
    setAnalisis({
      ...analisis,
      firma: nombreImagen
    });
  };

  const handleSelectLogo = (nombreImagen: string) => {
    if (!analisis) return;
    
    setAnalisis({
      ...analisis,
      logo: nombreImagen
    });
  };

  useEffect(() => {
    let vigente = true;
    cargarImagenComoDataUri(analisis?.firma).then(src => {
      if (vigente) setFirmaSrc(src);
    });
    return () => {
      vigente = false;
    };
  }, [analisis?.firma]);

  useEffect(() => {
    let vigente = true;
    cargarImagenComoDataUri(analisis?.logo).then(src => {
      if (vigente) setLogoSrc(src);
    });
    return () => {
      vigente = false;
    };
  }, [analisis?.logo]);

  if (loading) {
    return <LoadingState label="Cargando análisis..." className="h-96" />;
  }

  if (error) {
    return (
      <div className="flex items-start gap-3 bg-red-50 border border-red-200 rounded-md p-4 mt-4">
        <ExclamationTriangleIcon className="h-5 w-5 text-red-500 flex-shrink-0 mt-0.5" />
        <div>
          <p className="text-sm text-red-700">{error}</p>
          <Button variant="outline" size="sm" onClick={handleCancel} className="mt-3">
            Volver a análisis pendientes
          </Button>
        </div>
      </div>
    );
  }

  if (!analisis) {
    return (
      <div className="flex items-start gap-3 bg-amber-50 border border-amber-200 rounded-md p-4 mt-4">
        <ExclamationTriangleIcon className="h-5 w-5 text-amber-500 flex-shrink-0 mt-0.5" />
        <div>
          <p className="text-sm text-amber-700">No se encontró información para este análisis</p>
          <Button variant="outline" size="sm" onClick={handleCancel} className="mt-3">
            Volver a análisis pendientes
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full">
      {success && (
        <div className="flex items-start gap-3 bg-green-50 border border-green-200 rounded-md p-4 mb-4">
          <CheckCircleIcon className="h-5 w-5 text-green-500 flex-shrink-0 mt-0.5" />
          <p className="text-sm text-green-700">Análisis actualizado correctamente</p>
        </div>
      )}

      {/* Modal de confirmación */}
      {showConfirmModal && (
        <Modal
          title="Confirmar finalización"
          onClose={() => setShowConfirmModal(false)}
          size="sm"
          footer={
            <div className="flex justify-end space-x-3">
              <Button variant="outline" onClick={() => setShowConfirmModal(false)}>
                Cancelar
              </Button>
              <Button
                variant="primary"
                onClick={() => {
                  setShowConfirmModal(false);
                  handleSave();
                }}
              >
                Confirmar y guardar
              </Button>
            </div>
          }
        >
          <div className="text-sm text-slate-600">
            <p className="mb-3">
              ¡Atención! Una vez guardados estos cambios, el estado del análisis pasará a "finalizado" y <strong>no podrá volver a modificarse</strong>.
            </p>
            {(!analisis?.firma || analisis.firma === 'no especifica.png') && (
              <div className="flex items-start gap-2 p-3 bg-amber-50 border border-amber-200 rounded-md mb-3">
                <ExclamationTriangleIcon className="h-4 w-4 text-amber-500 flex-shrink-0 mt-0.5" />
                <p className="text-amber-800 text-sm">
                  Nota: No se ha seleccionado firma. El análisis se guardará sin firma.
                </p>
              </div>
            )}
            <p>¿Desea continuar?</p>
          </div>
        </Modal>
      )}

      <div className="bg-white border border-gray-300 shadow-lg rounded-md w-full max-w-5xl mx-auto my-6" style={{ minHeight: '29.7cm', width: '21cm' }}>
        {/* Cabecera */}
        <div className="border-b border-gray-300 p-6 flex justify-between items-center">
          <div className="flex items-center">
            <NeaLogo height={44} className="mr-4" />
            <h1 className="text-2xl font-bold text-gray-800 mr-4">Carga de Ensayos</h1>
            
            {/* Logo */}
            <div className="ml-4">
              {analisis.logo && analisis.logo !== 'no especifica.png' ? (
                <div className="relative w-24 h-16 border rounded overflow-hidden">
                  {logoSrc ? (
                    <img src={logoSrc} alt="Logo" className="w-full h-full object-contain" />
                  ) : (
                    <span className="flex h-full w-full items-center justify-center px-1 text-center text-[10px] text-amber-700">
                      Logo no disponible
                    </span>
                  )}
                  <button
                    onClick={() => {
                      if (analisis) {
                        setAnalisis({
                          ...analisis,
                          logo: ''
                        });
                      }
                    }}
                    className="absolute top-0 right-0 p-1 bg-white bg-opacity-70 rounded-bl"
                  >
                    <svg className="w-4 h-4 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              ) : (
                <button
                  onClick={() => setShowLogoModal(true)}
                  className="px-3 py-2 bg-blue-50 border border-blue-200 text-sm rounded hover:bg-blue-100 flex items-center"
                >
                  <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                  Logo
                </button>
              )}
            </div>
          </div>
          <div className="text-right">
            <p className="text-xs text-gray-500">Análisis N°</p>
            <p className="text-lg font-bold text-gray-800">{analisis.numero_nea || 'N/A'}</p>
            <p className="text-gray-600">Fecha: {formatFecha(new Date())}</p>
          </div>
        </div>

        {/* Información principal - Secciones Solicitante y Laboratorio (lado a lado) */}
        <div className="p-6 border-b border-gray-300">
          <div className="grid grid-cols-2 gap-6">
            {/* Sección del Solicitante */}
            <div className="border-r pr-6">
              <h2 className="text-lg font-bold text-gray-800 mb-4">Información del Solicitante</h2>
              <div className="space-y-3">
                <div>
                  <p className="text-sm font-medium text-gray-700">Solicitante:</p>
                  <p className="text-gray-900">{analisis.solicitante}</p>
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-700">CUIT:</p>
                  <p className="text-gray-900">{analisis.cuit_sol}</p>
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-700">Dirección:</p>
                  <p className="text-gray-900">{analisis.direccion_sol}</p>
                </div>
                {analisis.planta && (
                  <div>
                    <p className="text-sm font-medium text-gray-700">Planta:</p>
                    <p className="text-gray-900">{analisis.planta}</p>
                  </div>
                )}
                {analisis.mostrar_numero_origen && (
                  <div>
                    <p className="text-sm font-medium text-gray-700">Número análisis origen:</p>
                    <p className="text-gray-900">{analisis.numero_analisis_origen}</p>
                  </div>
                )}
              </div>
            </div>
            
            {/* Sección del Laboratorio */}
            <div>
              <h2 className="text-lg font-bold text-gray-800 mb-4">Información del Laboratorio</h2>
              <div className="space-y-3">
                <div>
                  <p className="text-sm font-medium text-gray-700">Laboratorio:</p>
                  <p className="text-gray-900">{analisis.laboratorio}</p>
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-700">CUIT:</p>
                  <p className="text-gray-900">{analisis.cuit_lab}</p>
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-700">Dirección:</p>
                  <p className="text-gray-900">{analisis.direccion_lab}</p>
                </div>
                {analisis.mostrar_numero_laboratorio && (
                  <div>
                    <p className="text-sm font-medium text-gray-700">Número análisis laboratorio:</p>
                    <p className="text-gray-900">{analisis.numero_analisis_laboratorio}</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Fechas */}
        <div className="p-6 border-b border-gray-300">
          <h2 className="text-lg font-bold text-gray-800 mb-4">Fechas</h2>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <p className="text-sm font-medium text-gray-700">Fecha de análisis:</p>
              <p className="text-gray-900">{analisis.fecha_analisis}</p>
            </div>
            <div>
              <p className="text-sm font-medium text-gray-700">Fecha de muestreo:</p>
              {/* Se define al solicitar el análisis y no cambia al cargar los
                  ensayos: cada muestra muestra la suya más abajo. */}
              <p className="text-gray-900">Ver el detalle de cada muestra</p>
            </div>
          </div>
        </div>

        {/* Muestras y Ensayos */}
        <div className="p-6">
          <h2 className="text-lg font-bold text-gray-800 mb-4">Muestras y Ensayos</h2>
          
          {analisis.muestras.map((muestra, muestraIndex) => (
            <div key={muestraIndex} className="mb-8 border rounded-lg p-4">
              <h3 className="font-bold text-gray-700 mb-2">Muestra {muestraIndex + 1}</h3>
              
              {/* Información de la muestra */}
              <div className="grid grid-cols-2 gap-4 mb-4">
                <div>
                  <p className="text-sm font-medium text-gray-700">ID NEA:</p>
                  <p className="text-gray-900">{muestra.id_nea}</p>
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-700">ID de Muestra de Origen:</p>
                  <p className="text-gray-900">{muestra.id_m_origen}</p>
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-700">ID de Muestra de Laboratorio:</p>
                  <p className="text-gray-900">{muestra.id_m_lab}</p>
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-700">Nombre del responsable:</p>
                  <p className="text-gray-900">{muestra.responsable}</p>
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-700">Sitio de muestreo:</p>
                  <p className="text-gray-900">{muestra.sitio_muestreo}</p>
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-700">Medio:</p>
                  <p className="text-gray-900">{muestra.medio}</p>
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-700">Pertenece a:</p>
                  <p className="text-gray-900">{muestra.pertenencia}</p>
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-700">Fecha muestreo:</p>
                  <p className="text-gray-900">{muestra.fecha_muestreo}</p>
                </div>
                {(tieneCoordenada(muestra.georr_lat) || tieneCoordenada(muestra.georr_long)) && (
                  <div className="col-span-2">
                    <p className="text-sm font-medium text-gray-700">Coordenadas:</p>
                    <p className="text-gray-900">
                      {tieneCoordenada(muestra.georr_lat) && `Lat: ${muestra.georr_lat}`}
                      {tieneCoordenada(muestra.georr_lat) && tieneCoordenada(muestra.georr_long) && ', '}
                      {tieneCoordenada(muestra.georr_long) && `Long: ${muestra.georr_long}`}
                    </p>
                  </div>
                )}
                {muestra.mostrar_organismo_control && (
                  <div>
                    <p className="text-sm font-medium text-gray-700">Organismo de control:</p>
                    <p className="text-gray-900">{muestra.organismo_control}</p>
                  </div>
                )}
                {muestra.mostrar_cadena_custodia && (
                  <div>
                    <p className="text-sm font-medium text-gray-700">Cadena de custodia:</p>
                    <p className="text-gray-900">{muestra.cadena_custodia}</p>
                  </div>
                )}
              </div>
              
              {/* Ensayos de la muestra - Aquí es donde se cargan/modifican */}
              <div className="mt-4">
                <h4 className="font-medium text-gray-700 mb-2">Ensayos</h4>
                
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Analito</th>
                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Resultado</th>
                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Unidad</th>
                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Metodo</th>
                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Limite Deteccion</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {muestra.ensayos.map((ensayo, ensayoIndex) => (
                        <tr key={ensayoIndex}>
                          <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-900">
                            {ensayo.analito}
                          </td>
                          <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-900">
                            <input
                              type="text"
                              value={ensayo.resultado}
                              onChange={(e) => handleResultadoChange(muestraIndex, ensayoIndex, e.target.value)}
                              className="border-gray-300 focus:ring-blue-500 focus:border-blue-500 block w-full rounded-md sm:text-sm bg-gray-100"
                              placeholder="Resultado (numérico)"
                              required
                            />
                          </td>
                          <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-900">
                            {ensayo.unidad}
                          </td>
                          <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-900">
                            <input
                              type="text"
                              value={ensayo.metodo}
                              onChange={(e) => handleUpdateEnsayo(muestraIndex, ensayoIndex, 'metodo', e.target.value)}
                              className="border-gray-300 focus:ring-blue-500 focus:border-blue-500 block w-full rounded-md sm:text-sm bg-gray-100"
                              placeholder="Método"
                            />
                          </td>
                          <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-900">
                            <div className="col-span-1">
                              <input
                                type="text"
                                value={ensayo.limite_deteccion}
                                onChange={(e) => handleLimiteDeteccionChange(muestraIndex, ensayoIndex, e.target.value)}
                                className="w-full p-2 border rounded bg-gray-100"
                                placeholder="Límite de detección"
                              />
                            </div>
                          </td>
                        </tr>
                      ))}
                      {muestra.ensayos.length === 0 && (
                        <tr>
                          <td colSpan={5} className="px-3 py-2 text-center text-sm text-gray-500">
                            No hay ensayos para esta muestra
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
              
              {/* Observaciones de la muestra */}
              <div className="mt-4">
                <h4 className="font-medium text-gray-700 mb-2">Observaciones de la muestra</h4>
                <textarea
                  value={muestra.descripcion || ''}
                  onChange={(e) => {
                    if (!analisis) return;
                    const updatedMuestras = [...analisis.muestras];
                    updatedMuestras[muestraIndex] = {
                      ...updatedMuestras[muestraIndex],
                      descripcion: e.target.value
                    };
                    setAnalisis({
                      ...analisis,
                      muestras: updatedMuestras
                    });
                  }}
                  className="w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 bg-gray-100"
                  rows={3}
                  placeholder="Ingrese observaciones específicas para esta muestra"
                ></textarea>
              </div>
            </div>
          ))}
        </div>

        {/* Firmas y Analistas */}
        <div className="p-6 border-t border-gray-300">
          <h2 className="text-lg font-bold text-gray-800 mb-4">Analistas y Firmas</h2>
          
          <div className="grid grid-cols-2 gap-6">
            {/* Analistas */}
            <div>
              <h3 className="font-medium text-gray-700 mb-2">
                Analistas <span className="text-red-500">*</span>
              </h3>
              
              <div className="space-y-3">
                {analistasTexto.map((analista, index) => (
                  <div key={index} className="flex items-center space-x-2">
                    <input
                      type="text"
                      value={analista}
                      onChange={(e) => handleAnalistaChange(index, e.target.value)}
                      ref={el => { analistasInputRefs.current[index] = el; }}
                      className="flex-1 border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 bg-gray-100"
                      placeholder="Nombre completo del analista"
                    />
                    <button
                      type="button"
                      onClick={() => eliminarAnalista(index)}
                      className="p-1 bg-red-100 text-red-600 rounded hover:bg-red-200"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                      </svg>
                    </button>
                  </div>
                ))}
                
                <button
                  type="button"
                  onClick={agregarAnalista}
                  className="px-3 py-1 bg-blue-100 text-blue-700 rounded hover:bg-blue-200 flex items-center text-sm"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M10 5a1 1 0 011 1v3h3a1 1 0 110 2h-3v3a1 1 0 11-2 0v-3H6a1 1 0 110-2h3V6a1 1 0 011-1z" clipRule="evenodd" />
                  </svg>
                  Agregar analista
                </button>

                <p className="text-gray-500" style={{ fontSize: '0.9rem' }}>
                  Obligatorio: cargue el nombre de al menos un analista.
                </p>
              </div>
            </div>
            
            {/* Firma */}
            <div>
              <h3 className="font-medium text-gray-700 mb-2">
                Firma <span className="text-sm text-gray-500 font-normal">(Opcional)</span>
              </h3>
              <div className="mb-4">
                {analisis.firma && analisis.firma !== 'no especifica.png' ? (
                  <div className="relative w-40 h-20 border rounded overflow-hidden">
                    {firmaSrc ? (
                      <img src={firmaSrc} alt="Firma" className="w-full h-full object-contain" />
                    ) : (
                      <span className="flex h-full w-full items-center justify-center px-1 text-center text-[10px] text-amber-700">
                        Firma no disponible
                      </span>
                    )}
                    <button
                      onClick={() => {
                        if (analisis) {
                          setAnalisis({
                            ...analisis,
                            firma: ''
                          });
                        }
                      }}
                      className="absolute top-0 right-0 p-1 bg-white bg-opacity-70 rounded-bl"
                    >
                      <svg className="w-4 h-4 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>
                ) : (
                  <div>
                    <div className="flex space-x-2 mb-2">
                      <button
                        onClick={() => setShowFirmaModal(true)}
                        className="px-3 py-2 bg-blue-50 border border-blue-200 text-sm rounded hover:bg-blue-100 flex items-center"
                      >
                        <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                        </svg>
                        Nueva firma
                      </button>
                      
                      <button
                        onClick={() => {
                          cargarFirmasLaboratorio();
                          setShowLabFirmasModal(true);
                        }}
                        className="px-3 py-2 bg-green-50 border border-green-200 text-sm rounded hover:bg-green-100 flex items-center"
                      >
                        <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                        </svg>
                        Firmas anteriores
                      </button>
                    </div>
                    <div className="p-3 bg-blue-50 border border-blue-200 rounded-md">
                      <p className="text-sm text-blue-800">
                        <svg className="w-4 h-4 inline mr-2" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                        </svg>
                        Sin firma seleccionada. Puede guardar los cambios sin firma o seleccionar una si lo desea.
                      </p>
                    </div>
                  </div>
                )}
                <p className="text-xs text-gray-500 mt-2">La firma es opcional. Puede guardar el análisis sin firma.</p>
              </div>
            </div>
            
            {/* Nombre Director */}
            <div>
              <h3 className="font-medium text-gray-700 mb-2">
                Nombre Director <span className="text-red-500">*</span>
              </h3>
              <div className="mb-4">
                <input
                  type="text"
                  value={analisis.director || ''}
                  onChange={(e) => {
                    if (!analisis) return;
                    setAnalisis({
                      ...analisis,
                      director: e.target.value
                    });
                  }}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Ingrese el nombre del director"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Obligatorio: nombre del director responsable del análisis.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Botones de acción */}
        {validacion && (
          <div className="mx-6 flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 p-3">
            <ExclamationTriangleIcon className="h-5 w-5 flex-shrink-0 text-amber-500" />
            <p className="text-sm text-amber-800">{validacion}</p>
          </div>
        )}
        <div className="p-6 border-t border-gray-300 flex justify-end space-x-4">
          <Button variant="outline" onClick={handleCancel} disabled={saving}>
            Cancelar
          </Button>
          <Button variant="primary" onClick={confirmarGuardado} loading={saving} disabled={saving}>
            {saving ? 'Guardando...' : 'Guardar cambios'}
          </Button>
        </div>
      </div>

      {/* Agregar un nuevo modal para seleccionar firmas del laboratorio */}
      {showLabFirmasModal && (
        <Modal title="Seleccionar Firma Anterior" onClose={() => setShowLabFirmasModal(false)} size="lg">
          {loadingFirmas ? (
            <LoadingState label="Cargando firmas..." />
          ) : firmasLaboratorio.length > 0 ? (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4 overflow-y-auto max-h-80">
              {firmasLaboratorio.map((firma) => (
                <div
                  key={firma.nombre}
                  className="relative border rounded-md p-2 cursor-pointer hover:border-brand-500"
                  onClick={() => {
                    handleSelectFirma(firma.nombre);
                    setShowLabFirmasModal(false);
                  }}
                >
                  <div className="w-full pb-[100%] relative">
                    <img
                      src={firma.urlConTimestamp || `${api.defaults.baseURL}${firma.url}`}
                      alt={firma.nombre}
                      className="absolute inset-0 w-full h-full object-contain"
                      onError={(e) => {
                        // Sin imagen de reemplazo externa: se oculta y queda el
                        // nombre del análisis como referencia.
                        e.currentTarget.style.display = 'none';
                      }}
                    />
                  </div>
                  <p className="text-xs text-center mt-2 truncate">
                    {firma.analisisNea || `Análisis #${firma.analisisId}`}
                  </p>
                </div>
              ))}
            </div>
          ) : (
            <EmptyState
              title="No hay firmas anteriores disponibles"
              action={
                <Button
                  variant="primary"
                  size="sm"
                  onClick={() => {
                    setShowLabFirmasModal(false);
                    setShowFirmaModal(true);
                  }}
                >
                  Cargar una nueva firma
                </Button>
              }
            />
          )}
        </Modal>
      )}

      {/* Modales existentes sin cambios */}
      <ImagenSelectorModal 
        isOpen={showFirmaModal}
        onClose={() => setShowFirmaModal(false)}
        onSelect={handleSelectFirma}
        tipo="firma"
      />

      <ImagenSelectorModal 
        isOpen={showLogoModal}
        onClose={() => setShowLogoModal(false)}
        onSelect={handleSelectLogo}
        tipo="logo"
      />
    </div>
  );
};

export default CargarEnsayos;
